// Local Encrypted Offline Vault & Document Archive for Deep Research Hub

export interface VaultDocument {
  id: string;
  agentId: string;
  agentName: string;
  title: string;
  category: string;
  jurisdiction: string;
  language: string;
  createdAt: string;
  fileType: 'docx' | 'xlsx' | 'pdf' | 'json';
  summary: string;
  contentSnippet: string;
  tags: string[];
  localFilePath?: string;
  signedByToken?: string;
}

const VAULT_STORAGE_KEY = 'drh_local_document_vault_v1';

export const getVaultDocuments = (): VaultDocument[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(VAULT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (err) {
    console.warn('Error reading local vault:', err);
  }
  return [];
};

export const saveDocumentToVault = (doc: Omit<VaultDocument, 'id' | 'createdAt'>): VaultDocument => {
  const existing = getVaultDocuments();
  const newDoc: VaultDocument = {
    ...doc,
    id: `doc_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    createdAt: new Date().toISOString()
  };

  const updated = [newDoc, ...existing].slice(0, 300); // Keep last 300 reports
  try {
    localStorage.setItem(VAULT_STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Error writing to local vault:', err);
  }
  return newDoc;
};

export const searchVaultDocuments = (
  query: string,
  filterCategory?: string,
  filterJurisdiction?: string
): VaultDocument[] => {
  const docs = getVaultDocuments();
  const q = query.toLowerCase().trim();

  return docs.filter(doc => {
    const matchesQuery = !q || 
      doc.title.toLowerCase().includes(q) ||
      doc.agentName.toLowerCase().includes(q) ||
      doc.summary.toLowerCase().includes(q) ||
      doc.tags.some(t => t.toLowerCase().includes(q));

    const matchesCategory = !filterCategory || filterCategory === 'all' || doc.category === filterCategory;
    const matchesJurisdiction = !filterJurisdiction || filterJurisdiction === 'all' || doc.jurisdiction === filterJurisdiction;

    return matchesQuery && matchesCategory && matchesJurisdiction;
  });
};

export const deleteVaultDocument = (id: string): void => {
  const docs = getVaultDocuments();
  const updated = docs.filter(d => d.id !== id);
  try {
    localStorage.setItem(VAULT_STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Error deleting from vault:', err);
  }
};
